// src/modals/IncidentModal.jsx — modale création/édition d'un incident
// (multi-dégradations).
//
// Verrouillage côté ménage (cf. PR #10) :
//   - lockedDeg(deg)     : dégradation déjà traitée par admin (remplaceLe rempli) → figée
//   - incidentLocked     : si ≥1 deg traitée → champs globaux gelés (logement, voyageur, date)
//   - bloc SUIVI complet visible admin uniquement
//   - bloc SUIVI lecture-seule côté ménage si dég traitée (juste la date "Remplacé le")
//
// PR #151 PARTIE E (ADR-084) — verrouillage anti-anticipation :
//   - dateLocked         : prop legacy (boolean). Si effectiveRunStatus
//                          fourni → ignoré (l'unification Q1 prime).
//
// PR #153 (ADR-087/088) — refonte workflow :
//   - effectiveRunStatus : prop unifiée (objet { key, label, ... } depuis
//                          getEffectiveRunStatus). Si key === 'upcoming'
//                          + !isAdmin → fullyLocked. Cohérent avec
//                          ChecklistRunModal (verrou unifié Q1 Dimitri).
//   - run                : run optionnel (carte run-contextuelle). Si fourni
//                          + isWithinIncidentWindow → bandeau pédagogique
//                          fenêtre de signalement tardive 48h (ADR-088).
//   - initialStatus      : pré-remplir radio dégradation par défaut.
//   - PHOTOS_MIN=2       : règle ménage. Compteur UI + disable Save si <2.
//                          Le trigger DB INSERT photos_min duplique côté serveur.
//   - submitting state   : M-1 anti double-click.
//   - Q5 warning admin photos=0 non-bloquant.
//
// PR #153 commit 8 (R-1 fix) — mini-queue photos pré-INSERT côté ménage :
//   - Au mount du modal en mode run-contextuel (ménage + run fourni),
//     génération d'un tmpIncidentId UUID client.
//   - Bouton "📸 Ajouter une photo" → upload immédiat vers Storage via
//     enqueueIncidentPhoto (path stable run-{runId}/incident-{tmpId}/).
//   - State uploadedPhotos contient { path, publicUrl, fileName }.
//   - handleCancel → cleanupIncidentPhotos best-effort si photos uploadées
//     mais incident pas créé.
//   - handleSave → INSERT incident avec photos = [paths Storage].
//   - Le mode admin manuel garde le pattern legacy (props photos read-only,
//     pas de queue ; admin uploadera via process 2-phase post-INSERT).
//
// Filtrage logIds : caller passe userLogIds (currentUser.logIds || []) pour
// restreindre le sélecteur logement côté ménage. Inclut les chambres sous-loc.
//
// Validation handleSave : logId requis, voyageur requis ménage uniquement,
// chaque dégradation doit avoir un type, photos>=2 ménage.

import { useRef, useState, useMemo, useEffect } from "react"
import { AlertTriangle, Calendar, Clock, Lightbulb, Camera, Check, Search, Ban, Lock, Save, Loader2, Plus } from "lucide-react"
import { DS } from "../lib/ui"
import { INCIDENT_TYPES, MENAGE_STATUS_COLOR, ETATS_FINANCIERS, ETATS_FINANCIERS_SOUSLOC } from "../lib/domain-constants"
import { uid } from "../lib/format"
import { getDegs, deriveEtatFinancier } from "../lib/incidents-helpers"
import { getAllLogsFlat } from "../lib/sousloc-helpers"
import { formatDateFR } from "../lib/format"
import { isWithinIncidentWindow, hoursRemainingInIncidentWindow } from "../lib/incidents-window"
import { isRunOpenForMenageToday } from "../lib/menage-status-helpers"
import { todayParisIso } from "../views/MenageTodayView"
import { enqueueIncidentPhoto, cleanupIncidentPhotos, removeIncidentPhoto } from "../lib/menage-photo-queue"
// Lot 5 — timeline admin (déclaration + changements de statut) + badge requalif.
import { IncidentTimeline, RequalifBadge } from "../components/IncidentTimeline"
import useEchapPourFermer from "../lib/useEchapPourFermer"

const PHOTOS_MIN_MENAGE = 2

// Lot 5 — section timeline pour la modale admin : badge requalif (dérivé) +
// historique repliable. Une seule requête (IncidentTimeline.onLoaded remonte la
// timeline au badge).
function IncidentTimelineSection({ incidentId }) {
  const [tl, setTl] = useState(null)
  return (
    <div>
      <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginBottom:4}}>
        <span style={{fontSize:11,fontWeight:700,color:DS.marine,textTransform:"uppercase",letterSpacing:.5}}>Historique</span>
        {tl && <RequalifBadge timeline={tl} />}
      </div>
      <IncidentTimeline incidentId={incidentId} onLoaded={setTl} />
    </div>
  )
}

export default function IncidentModal({
  zIndexBase = 999, // relevé (1200) quand la modale s'ouvre AU-DESSUS de la checklist
  incident,
  cfg,
  data,
  onSave,
  onClose,
  defaultLogId,
  isAdmin,
  userLogIds,
  // Legacy verrouillage (PR #151 PARTIE E)
  dateLocked = false,
  lockedDateIso = null,
  // PR #153 (ADR-087) — verrou heure unifié + fenêtre de signalement 48h
  effectiveRunStatus = null,
  run = null,
  initialStatus = null,
  // PR #153 — photos courantes (ménage upload via queue côté caller).
  // L'array peut contenir des objets { url, ... } ou des string URLs.
  photos: photosProp = null,
}) {
  // Échap ferme — consigne 2026-08-14, hook partagé (pile anti-double-fermeture).
  useEchapPourFermer(onClose)
  // Liste des logements/chambres feuilles (helper sous-loc — un incident sous-loc
  // est attribué à une chambre, pas au log parent).
  // Filtrage logIds :
  //   - admin : tous logements + chambres
  //   - ménage : uniquement les ids présents dans userLogIds
  //              userLogIds = [] → liste vide → save bloqué par la validation logId.
  const allLogsRaw = getAllLogsFlat(cfg)
  const allLogs = isAdmin
    ? allLogsRaw
    : allLogsRaw.filter(l => (userLogIds || []).includes(l.id))
  const findPropId = lid => allLogsRaw.find(l => l.id === lid)?.propId || ""

  // Phase 4 cascade PR #134/137 : "Récupérable" → "À vérifier" (label
  // ménage), même valeur DB pour rétrocompat archives V1.
  // PR #153 : initialStatus prop pour pré-remplir.
  // ADR-228 — le JUGEMENT par dégradation passe de l'agent à l'orchestrateur :
  // une déclaration AGENT (non-admin) part TOUJOURS de « À vérifier » (défaut
  // neutre), JAMAIS d'un initialStatus qui pourrait pré-juger. L'admin garde
  // initialStatus (pré-remplissage du suivi). Le défaut ne dépend plus du bloc
  // « État » (retiré côté agent ci-dessous).
  const defaultStatutMenage = isAdmin ? (initialStatus || "À vérifier") : "À vérifier"
  // Chantier ⑥ — le champ `etatFinancier` (5 états financiers) REMPLACE l'ancien
  // `traitement` (fbz/info). Défaut = null (« À qualifier » — aucun des 5 états ;
  // l'admin qualifie). L'ancien `traitement` n'est plus émis.
  const emptyDeg = () => ({
    id: uid(),
    type: "",
    nombre: "1",
    precision: "",
    statutMenage: defaultStatutMenage,
    remplaceLe: "",
    etatFinancier: null,
    cout: "",
    // Refonte 2026-08-13 : les photos vivent PAR DÉGRADATION (2 minimum par
    // article — la garde en base nomme l'article fautif). [{ path, url,
    // fileName, exifDate, exifAppareil, originale, previewUrl }]
    photos: [],
  })

  // À l'édition, on dérive `etatFinancier` depuis l'ancien `traitement` si absent
  // (rétrocompat) pour que le sélecteur reflète l'état existant et le re-persiste.
  const initDegs = incident
    ? getDegs(incident).map(d => ({ ...d, etatFinancier: d.etatFinancier ?? deriveEtatFinancier(d) }))
    : [emptyDeg()]

  const [logId, setLogId] = useState(incident?.logId || defaultLogId || "")
  const [voyageur, setVoyageur] = useState(incident?.voyageur || "")
  // « Aucun voyageur en cause » — la casse INTERNE (l'équipe a cassé). Masque
  // le champ voyageur, lève son caractère obligatoire, et le propriétaire ne
  // verra JAMAIS cet incident (filtre serveur). Posable par l'agent (socle).
  const [casseInterne, setCasseInterne] = useState(!!incident?.casse_interne)
  const [date, setDate] = useState(incident?.date || new Date().toISOString().split('T')[0])
  // V4 lot D — champ « Déclaré sur Superhote » retiré de l'UI (plus de case à
  // cocher). State conservé par prudence : le payload onSave reste identique
  // pour l'appelant. NB (pessimist) : le flux V2 (IncidentsTab) ne persiste de
  // toute façon jamais ce champ (v2ToKvShape le force à false) — champ inerte
  // côté base, aucun round-trip DB à préserver.
  const [superhote] = useState(incident?.superhote || false)
  const [degs, setDegs] = useState(initDegs.length ? initDegs : [emptyDeg()])
  // M-1 — anti double-click submit. submittingRef est un verrou synchrone
  // (state React étant async, 3 clicks dans le même tick verraient tous
  // submitting=false). useRef garantit l'idempotence anti-race-condition.
  const [submitting, setSubmitting] = useState(false)
  // ÉCRAN D'ACCUSÉ (agent) : après l'envoi, on ne referme pas sèchement — on
  // dit ce qui vient de se passer et OÙ SUIVRE. La phrase est celle de Dimitri,
  // au mot près : on ne promet pas une notification qui n'existe pas.
  const [accuse, setAccuse] = useState(false)
  const submittingRef = useRef(false)
  const [error, setError] = useState(null)

  // PR #153 commit 8 (R-1) — mini-queue photos pré-INSERT côté ménage.
  // Si on est dans un contexte run-contextuel ménage (run fourni + !isAdmin),
  // on active la mini-queue : génération d'un tmpIncidentId stable (1 par
  // session modal) et state local uploadedPhotos.
  const useMenageMiniQueue = !!run && !isAdmin
  // useMemo pour stabilité du tmpIncidentId à travers les renders (ne
  // change pas si crypto/run change). useState init lazy ferait pareil
  // mais useMemo est plus lisible ici (pas d'updater).
  const tmpIncidentIdRef = useRef(null)
  if (useMenageMiniQueue && !tmpIncidentIdRef.current) {
    tmpIncidentIdRef.current = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : uid()
  }
  const tmpIncidentId = tmpIncidentIdRef.current

  // uploadedPhotos : photos uploadées via la mini-queue ménage (state local).
  // Format : [{ path, publicUrl, fileName }]
  const [uploadedPhotos, setUploadedPhotos] = useState([])
  // Lot 2 photo (ADR-209) : objectURL d'aperçu à révoquer au démontage (anti-fuite).
  const previewUrlsRef = useRef([])
  useEffect(() => () => {
    previewUrlsRef.current.forEach((u) => { try { URL.revokeObjectURL(u) } catch (_) { /* noop */ } })
  }, [])
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef(null)

  // Photos affichées dans le compteur :
  // - mini-queue ménage actif → uploadedPhotos local
  // - sinon (admin manuel ou caller legacy) → photosProp
  const photos = useMemo(() => {
    if (useMenageMiniQueue) return uploadedPhotos
    return Array.isArray(photosProp) ? photosProp : []
  }, [useMenageMiniQueue, uploadedPhotos, photosProp])
  const photosCount = photos.length

  const si = { border:`0.5px solid ${DS.borderHover}`,borderRadius:DS.radiusControl,padding:"9px 12px",width:"100%",fontSize:13,outline:"none",fontFamily:DS.fontBody,background:DS.card }
  const lbl = { fontSize:11,color:DS.textMuted,marginBottom:4,fontWeight:600,textTransform:"uppercase",letterSpacing:.5 }

  const updateDeg = (idx, key, val) => setDegs(d => d.map((item, i) => i === idx ? { ...item, [key]: val } : item))
  const addDeg = () => setDegs(d => [...d, emptyDeg()])
  const removeDeg = idx => setDegs(d => d.filter((_, i) => i !== idx))

  // Verrouillage côté ménage : une dégradation déjà traitée (`remplaceLe` saisi
  // par admin) n'est plus modifiable. Et dès qu'au moins une dégradation est
  // traitée, les champs globaux de l'incident sont gelés (logement, voyageur,
  // date). Admin n'est jamais verrouillé.
  const lockedDeg = (deg) => !isAdmin && !!deg.remplaceLe
  const incidentLocked = !isAdmin && degs.some(d => !!d.remplaceLe)

  // ADR-230 — verrou jour-J (remplace la base « heure de check-out » bloquante).
  // L'agent peut déclarer un incident dès 00:01 le jour J. EXEMPTION fenêtre 48h
  // post-complétion (MAJEUR 1.1 / ADR-088) : un run completed de la veille
  // (scheduled_date ≠ today) reste déclarable tant que withinIncidentWindow.
  // withinIncidentWindow est calculé plus bas — on le câble après sa déclaration.
  // `today` figé au render (cohérent ChecklistRunModal). `run` peut être null
  // (incident manuel admin / hors contexte run) → isRunOpenForMenageToday=false,
  // mais !isAdmin gère l'admin et le legacy dateLocked ci-dessous.
  const today = todayParisIso()

  // isUpcoming conservé UNIQUEMENT pour gater le bandeau « fenêtre 48h » (ne pas
  // l'afficher en même temps qu'un run encore à venir). ADR-230 : il ne pilote
  // plus fullyLocked (le verrou jour-J via timeLocked s'en charge).
  const isUpcoming = effectiveRunStatus
    ? (effectiveRunStatus.key === 'upcoming')
    : !!dateLocked
  const lockedStyle = { opacity: 0.55, cursor: "not-allowed" }

  // Format date courte "DD/MM" pour le bouton "🔒 Le DD/MM"
  const formatDateShort = (yyyymmdd) => {
    if (!yyyymmdd || !/^\d{4}-\d{2}-\d{2}$/.test(yyyymmdd)) return yyyymmdd || '?'
    const [, m, d] = yyyymmdd.split('-')
    return `${d}/${m}`
  }

  // Date affichée dans le bandeau verrou : run.scheduled_date prime sur lockedDateIso legacy
  const lockDateForBanner = run?.scheduled_date || lockedDateIso

  // PR #153 (ADR-088) — fenêtre de signalement tardive (48h, alignée verrou ménage).
  // Si run + completed_at + dans la fenêtre → bandeau bleu pédagogique
  // ("encore X heures pour signaler"). Pas un verrou, juste informatif.
  const withinIncidentWindow = run ? isWithinIncidentWindow(run) : false
  const hoursRemaining = withinIncidentWindow ? hoursRemainingInIncidentWindow(run) : 0

  // ADR-230 — verrou temporel côté ménage.
  //   - CONTEXTE RUN (run fourni, ex. déclaration depuis la fiche ménage du jour) :
  //     FERMÉ si on n'est NI le jour J (dès 00:01) NI dans la fenêtre 48h post-
  //     complétion (run completed récent, scheduled_date ≠ today). Le futur jour-J
  //     reste bloqué (égalité stricte dans isRunOpenForMenageToday).
  //   - HORS RUN (run null, ex. incident manuel admin / caller legacy) : on
  //     conserve la sémantique legacy `isUpcoming` (effectiveRunStatus.key ===
  //     'upcoming' OU dateLocked) — sinon un caller sans run serait verrouillé à
  //     tort (régression T-11b).
  // L'admin n'est JAMAIS verrouillé par ce biais.
  const timeLocked = !isAdmin && (
    run
      ? (!isRunOpenForMenageToday(run, today) && !withinIncidentWindow)
      : isUpcoming
  )
  // fullyLocked : verrou temporel OU dégradation déjà traitée par admin
  // (incidentLocked gèle les champs globaux). degLocked = idem mais le verrou
  // per-dégradation lockedDeg(deg) s'ajoute (parité avec l'existant : un deg
  // traité reste figé indépendamment du temps).
  const fullyLocked = incidentLocked || timeLocked
  const degLocked = (deg) => lockedDeg(deg) || timeLocked

  // Chantier ⑥ — état financier (5 états), gating admin + restriction sous-loc.
  // Sous-loc = chambre lc1/lc2/lc3 (parentLog attaché par getAllLogsFlat) OU
  // logement du proprio p6 « Maison Zen » OU facturationSource='v2'. En sous-loc,
  // FBZ est l'hôte Airbnb (encaisse l'AirCover, pas de facture proprio) → le
  // sélecteur est RESTREINT à « Sans coût » / « Achat FULLBNBZEN » + un champ
  // « AirCover perçu par FBZ » en atténuation de charge compta.
  const selectedLog = allLogsRaw.find(l => l.id === logId)
  const isSousLoc = !!selectedLog?.parentLog || selectedLog?.propId === "p6" || selectedLog?.facturationSource === "v2"
  const etatOptions = isSousLoc ? ETATS_FINANCIERS_SOUSLOC : ETATS_FINANCIERS
  // États porteurs d'un coût FBZ (champ Coût affiché) et états où le PROPRIO
  // perçoit l'AirCover (champ AirCover proprio affiché — hors sous-loc).
  const COUT_ETATS = new Set(["Refacturé propriétaire", "Achat FULLBNBZEN", "AirCover + refacturé"])
  const AIRCOVER_PROPRIO_ETATS = new Set(["Remboursé AirCover", "AirCover + refacturé"])

  // Auto-détection du voyageur depuis les réservations (logement + date)
  const autoDetectVoyageur = (lid, dateStr) => {
    if (!lid || !dateStr || !data) return
    // try/catch : une date de résa non parsable (toISOString jette) doit sauter
    // la détection, pas tuer la modale.
    try {
      const allResas = Object.values(data).flatMap(mk => (mk?.[lid]?.reservations || []))
      const match = allResas.find(r => {
        if (!r) return false
        const checkIn = r.checkIn || r.date || ''
        if (!checkIn) return false
        const checkOut = checkIn && r.nuits
          ? new Date(new Date(checkIn).getTime() + r.nuits * 86400000).toISOString().split('T')[0]
          : checkIn
        return dateStr >= checkIn && dateStr <= checkOut
      })
      const masquages = ['Reserved', '(Not available)', 'Not available']
      if (match && match.voyageur && match.voyageur !== '' && !masquages.includes(match.voyageur)) {
        setVoyageur(match.voyageur)
      }
    } catch (e) {
      console.error('autoDetectVoyageur', e)
    }
  }

  const handleLogChange = lid => {
    setLogId(lid)
    autoDetectVoyageur(lid, date)
  }

  // Refonte 2026-08-13 — 2 photos PAR ARTICLE (UI hint, garde DB par article).
  const photosMinNotMet = !isAdmin && useMenageMiniQueue
    && degs.some(d => (d.photos || []).length < PHOTOS_MIN_MENAGE)
  // Q5 admin photos=0 → warning non-bloquant (doc litige Aircover)
  const adminWarnNoPhotos = isAdmin && photosCount === 0

  // Refonte 2026-08-13 — upload PAR DÉGRADATION, DEUX CHEMINS (appareil +
  // galerie : deux <input> cachés, celui SANS capture ouvre la galerie). Le
  // fichier atterrit dans deg.photos de l'article visé (pendingDegIdxRef).
  const pendingDegIdxRef = useRef(null)
  const galleryInputRef = useRef(null)

  const handleAddPhotoDeg = async (degIdx, file) => {
    if (!useMenageMiniQueue) return
    if (uploading || fullyLocked) return
    if (!file) return
    setUploading(true)
    setError(null)
    try {
      // enqueueIncidentPhoto = « l'original d'abord » : ≤ 8 MB part BRUT (EXIF
      // préservés), extraction date/appareil AVANT compression, repli compressé
      // sur échec réseau. Le retour porte exifDate/exifAppareil/originale.
      const result = await enqueueIncidentPhoto({
        logementId: run.logement_id,
        runId: run.id,
        tmpIncidentId,
        file,
      })
      const previewUrl = URL.createObjectURL(file)
      previewUrlsRef.current.push(previewUrl)
      setDegs(d => d.map((item, i) => i === degIdx
        ? { ...item, photos: [...(item.photos || []), { ...result, previewUrl }] }
        : item))
      setUploadedPhotos(prev => [...prev, result]) // trace pour le cleanup à l'annulation
    } catch (err) {
      setError(`Upload photo échoué : ${err?.message || err}`)
    } finally {
      setUploading(false)
    }
  }

  const handleFileInput = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = '' // reset pour ré-upload même fichier si besoin
    const degIdx = pendingDegIdxRef.current
    pendingDegIdxRef.current = null
    if (file && degIdx != null) await handleAddPhotoDeg(degIdx, file)
  }

  const ouvrirAppareil = (degIdx) => { pendingDegIdxRef.current = degIdx; fileInputRef.current?.click() }
  const ouvrirGalerie = (degIdx) => { pendingDegIdxRef.current = degIdx; galleryInputRef.current?.click() }

  const handleRemovePhotoDeg = async (degIdx, photoIdx) => {
    if (!useMenageMiniQueue) return
    if (uploading || submitting) return
    const photo = (degs[degIdx]?.photos || [])[photoIdx]
    if (!photo?.path) return
    // Best-effort delete Storage (silencieux UX — si fail on garde l'item
    // décroché de l'UI, BACKLOG-5 nettoiera).
    await removeIncidentPhoto(photo.path)
    if (photo.previewUrl) URL.revokeObjectURL(photo.previewUrl)
    setDegs(d => d.map((item, i) => i === degIdx
      ? { ...item, photos: (item.photos || []).filter((_, j) => j !== photoIdx) }
      : item))
    setUploadedPhotos(prev => prev.filter(pp => pp.path !== photo.path))
  }

  const handleCancel = async () => {
    if (submitting || uploading) return
    // Cleanup Storage best-effort si ménage avait uploadé des photos sans
    // créer l'incident. Crash/refresh navigateur → BACKLOG-5 cleanup.
    if (useMenageMiniQueue && uploadedPhotos.length > 0) {
      try {
        await cleanupIncidentPhotos({
          logementId: run.logement_id,
          runId: run.id,
          tmpIncidentId,
        })
      } catch (_) { /* swallow */ }
    }
    onClose()
  }

  const handleSave = async () => {
    // Verrou synchrone via useRef (anti-race 3 clicks dans même tick)
    if (submittingRef.current || submitting || uploading || fullyLocked) return
    if (!logId) { setError("Sélectionne un logement."); return }
    // « Aucun voyageur en cause » (casse interne) lève l'obligation du nom.
    if (!isAdmin && !casseInterne && !voyageur.trim()) { setError("Le nom du voyageur est obligatoire — ou cochez « Aucun voyageur en cause »."); return }
    if (degs.some(d => !d.type)) { setError("Chaque dégradation doit avoir un type."); return }
    if (!degs.length) { setError("Au moins une dégradation est requise."); return }
    // ⚠️ 2 PHOTOS PAR ARTICLE — le message NOMME l'article fautif, comme la
    // garde en base (déclaration agent seulement ; l'admin n'est pas contraint).
    if (useMenageMiniQueue) {
      const fautive = degs.find(d => (d.photos || []).length < PHOTOS_MIN_MENAGE)
      if (fautive) {
        setError(`Chaque dégradation demande ${PHOTOS_MIN_MENAGE} photos minimum — il en manque sur « ${fautive.type || 'sans type'} ».`)
        return
      }
    }
    submittingRef.current = true
    setError(null)
    setSubmitting(true)
    try {
      const propId = findPropId(logId)
      // Les photos vivent PAR DÉGRADATION ; le champ global `photos` reste
      // alimenté (union) pour la compat des lecteurs existants (proprio,
      // galeries) — même donnée, deux angles de lecture.
      const degsForSave = degs.map(({ photos, ...d }) => ({
        ...d,
        photos: (photos || []).map(pp => ({
          url: pp.publicUrl ?? pp.url ?? null, path: pp.path, fileName: pp.fileName,
          exifDate: pp.exifDate ?? null, exifAppareil: pp.exifAppareil ?? null,
          originale: pp.originale ?? false,
        })),
      }))
      const photosForSave = useMenageMiniQueue
        ? degsForSave.flatMap(d => d.photos)
        : null
      await onSave({
        id: incident?.id || null,
        propId, logId,
        voyageur: casseInterne ? "" : voyageur,
        casse_interne: casseInterne,
        date, superhote,
        degradations: degsForSave,
        ...(photosForSave ? { photos: photosForSave } : {}),
      })
      // AGENT : écran d'accusé (on ne referme pas sèchement — l'appelant garde
      // la modale montée tant qu'on ne clique pas « Fermer »). ADMIN : l'appelant
      // ferme comme avant.
      if (useMenageMiniQueue) setAccuse(true)
    } catch (e) {
      setError(e?.message || String(e))
    } finally {
      setSubmitting(false)
      submittingRef.current = false
    }
  }

  // ═══ ÉCRAN D'ACCUSÉ (agent, après envoi) ═══
  // La phrase est celle de Dimitri, AU MOT PRÈS : « Suivez l'avancement dans
  // votre onglet Incidents » — on ne promet pas une notification qui n'existe pas.
  if (accuse) {
    return (
      <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.55)",zIndex:zIndexBase,display:"flex",alignItems:"flex-end",justifyContent:"center"}}>
        <div className="fbz-modal-sheet" data-testid="incident-accuse"
          style={{background:DS.card,borderRadius:"20px 20px 0 0",padding:"36px 24px",width:"100%",maxWidth:520,fontFamily:DS.fontBody,textAlign:"center"}}>
          <div style={{width:56,height:56,margin:"0 auto 14px",borderRadius:"50%",background:DS.successBg,display:"flex",alignItems:"center",justifyContent:"center"}}>
            <Check size={28} strokeWidth={2.6} color={DS.successText}/>
          </div>
          <div style={{fontFamily:DS.fontDisplay,fontSize:18,fontWeight:600,color:DS.marineDark,marginBottom:6}}>
            Incident transmis
          </div>
          <div style={{fontSize:13,color:DS.textSecondary,lineHeight:1.5,marginBottom:20}}>
            Suivez l'avancement dans votre onglet Incidents.
          </div>
          <button className="fbz-press" data-testid="incident-accuse-fermer" onClick={onClose}
            style={{width:"100%",padding:13,background:DS.marine,color:"#fff",border:"none",borderRadius:DS.radiusControl,fontWeight:700,fontSize:13,cursor:"pointer",fontFamily:DS.fontBody}}>
            Fermer
          </button>
        </div>
      </div>
    )
  }

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.55)",zIndex:zIndexBase,display:"flex",alignItems:"flex-end",justifyContent:"center"}}>
      <div className="fbz-modal-sheet" style={{background:DS.card,borderRadius:"20px 20px 0 0",padding:24,width:"100%",maxWidth:520,maxHeight:"92vh",overflowY:"auto",fontFamily:DS.fontBody}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18}}>
          <div style={{display:"flex",alignItems:"center",gap:8,fontFamily:DS.fontDisplay,fontWeight:600,color:DS.danger,fontSize:17}}><AlertTriangle size={18} strokeWidth={2.2}/>{incident ? "Modifier l'incident" : "Nouvel incident"}</div>
          <button onClick={handleCancel} disabled={submitting || uploading} style={{background:"none",border:"none",fontSize:24,cursor:(submitting||uploading)?"not-allowed":"pointer",color:DS.textMuted,lineHeight:1}}>×</button>
        </div>

        {/* Lot 5 — historique admin (déclaration + changements de statut, qui/quand)
            + badge requalif responsable. Lecture seule, dérivée d'audit_log
            (status only, 0 PII). Affiché uniquement à l'édition admin d'un
            incident V2 existant (incident.id présent). */}
        {isAdmin && incident?.id && (
          <div style={{marginBottom:14,padding:'10px 12px',background:DS.cielSoft,borderRadius:8}}>
            <IncidentTimelineSection incidentId={incident.id} />
          </div>
        )}

        {/* ADR-230 — bandeau « prévu » : affiché SEULEMENT si réellement verrouillé
            par le temps (timeLocked), i.e. run pas du jour ET hors fenêtre 48h.
            Plus de mention d'heure d'activation (le jour-J ouvre dès 00:01). */}
        {timeLocked && (
          <div style={{display:'flex',gap:9,alignItems:'flex-start',padding:'14px 16px',background:DS.warningBg,color:DS.warningText,borderRadius:8,fontSize:13,marginBottom:14,lineHeight:1.5,border:`1px solid ${DS.warning}55`}}>
            <Calendar size={16} strokeWidth={2.2} style={{flexShrink:0,marginTop:2}}/><span>Ce ménage est prévu le <strong>{lockDateForBanner ? formatDateFR(lockDateForBanner) : 'plus tard'}</strong>. Tu pourras signaler un incident le jour même.</span>
          </div>
        )}

        {/* PR #153 (ADR-088) — bandeau fenêtre de signalement tardive (48h). Run
            completed + dans les 48h après → ménage peut encore signaler.
            Informatif (non bloquant). */}
        {withinIncidentWindow && !isUpcoming && !isAdmin && (
          <div style={{display:'flex',gap:9,alignItems:'flex-start',padding:'12px 14px',background:DS.cielSoft,color:DS.marineDeep,borderRadius:8,fontSize:13,marginBottom:14,lineHeight:1.5,border:`1px solid ${DS.ciel}55`}}>
            <Clock size={16} strokeWidth={2.2} style={{flexShrink:0,marginTop:2}}/><span>Tu as encore <strong>{hoursRemaining}h</strong> pour signaler un incident sur ce ménage. Recharge l'app pour rafraîchir.</span>
          </div>
        )}

        {/* Bandeau erreur générique (validation/save) */}
        {error && (
          <div style={{display:'flex',gap:8,alignItems:'flex-start',padding:'10px 14px',background:DS.dangerBg,color:DS.dangerText,borderRadius:8,fontSize:12,marginBottom:12,lineHeight:1.4}}>
            <AlertTriangle size={14} strokeWidth={2.2} style={{flexShrink:0,marginTop:1}}/><span>{error}</span>
          </div>
        )}

        {/* Logement */}
        <div style={{marginBottom:12}}>
          <div style={lbl}>Logement</div>
          <select value={logId} onChange={e => handleLogChange(e.target.value)} disabled={fullyLocked}
            style={{...si, ...(fullyLocked ? lockedStyle : {})}}>
            <option value="">— Choisir —</option>
            {allLogs.map(l => <option key={l.id} value={l.id}>{l.nom}</option>)}
          </select>
        </div>

        {/* « Aucun voyageur en cause » — la casse INTERNE. Cochée : le champ
            voyageur disparaît (il n'y a personne à facturer) et le propriétaire
            ne verra jamais cet incident (filtre serveur). */}
        <label data-testid="incident-casse-interne"
          style={{display:"flex",alignItems:"center",gap:8,marginBottom:12,padding:"9px 12px",background:casseInterne?DS.cielSoft:DS.card,border:`1px solid ${casseInterne?DS.ciel:DS.border}`,borderRadius:8,cursor:fullyLocked?"not-allowed":"pointer",fontSize:12.5,fontWeight:600,color:DS.marineDark}}>
          <input type="checkbox" checked={casseInterne} disabled={fullyLocked}
            onChange={e => setCasseInterne(e.target.checked)}
            style={{width:16,height:16,accentColor:DS.marine}}/>
          Aucun voyageur en cause (casse interne)
        </label>

        {/* Voyageur + Date */}
        <div style={{display:"grid",gridTemplateColumns:casseInterne?"1fr":"1fr 1fr",gap:10,marginBottom:12}}>
          {!casseInterne && (
          <div>
            <div style={lbl}>Voyageur</div>
            <input value={voyageur} onChange={e => setVoyageur(e.target.value)} placeholder="Nom du voyageur"
              disabled={fullyLocked} style={{...si, ...(fullyLocked ? lockedStyle : {})}}/>
            {logId && date && !voyageur && <div style={{display:"flex",alignItems:"center",gap:5,fontSize:10,color:DS.textMuted,marginTop:3}}><Lightbulb size={12} strokeWidth={2.2}/>Sélectionne le logement + date pour auto-détecter</div>}
          </div>
          )}
          <div><div style={lbl}>Date</div><input type="date" value={date} onChange={e => { setDate(e.target.value); autoDetectVoyageur(logId, e.target.value) }} disabled={fullyLocked} style={{...si, ...(fullyLocked ? lockedStyle : {})}}/></div>
        </div>

        {/* Refonte 2026-08-13 — les photos vivent PAR DÉGRADATION (blocs plus
            bas). Ici ne restent que les DEUX CHEMINS d'entrée, cachés :
            l'appareil (capture) et la galerie (sans capture). */}
        {useMenageMiniQueue && (
          <>
            <input ref={fileInputRef} type="file" accept="image/*" capture="environment"
              onChange={handleFileInput} disabled={uploading || submitting || fullyLocked}
              style={{display:'none'}} data-testid="incident-input-appareil" />
            <input ref={galleryInputRef} type="file" accept="image/*"
              onChange={handleFileInput} disabled={uploading || submitting || fullyLocked}
              style={{display:'none'}} data-testid="incident-input-galerie" />
          </>
        )}

        {/* Q5 — warning admin photos=0 (non bloquant, ADR-087) */}
        {adminWarnNoPhotos && (
          <div style={{display:'flex',gap:8,alignItems:'flex-start',marginBottom:12,padding:'10px 12px',background:DS.warningBg,color:DS.warningText,borderRadius:8,border:`1px solid ${DS.warning}55`,fontSize:12,lineHeight:1.5}}>
            <AlertTriangle size={14} strokeWidth={2.2} style={{flexShrink:0,marginTop:1}}/><span>Aucune photo preuve. En cas de litige avec un voyageur ou de demande AirCover Airbnb, les photos sont essentielles.</span>
          </div>
        )}

        {/* ── Dégradations ── */}
        <div style={{marginBottom:16}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
            <div style={{fontSize:11,color:DS.textMuted,fontWeight:700,textTransform:"uppercase",letterSpacing:.5}}>Dégradations</div>
            <button onClick={addDeg} disabled={fullyLocked} className="fbz-press" style={{display:"inline-flex",alignItems:"center",gap:5,background:DS.corailSoft,color:DS.corailText,border:`0.5px solid ${DS.corail}55`,borderRadius:DS.radiusControl,padding:"5px 12px",fontSize:11,fontWeight:700,cursor:fullyLocked?"not-allowed":"pointer",fontFamily:DS.fontBody, ...(fullyLocked?lockedStyle:{})}}><Plus size={13} strokeWidth={2.4}/>Ajouter</button>
          </div>

          {degs.map((deg, idx) => (
            <div key={deg.id} style={{background:DS.dangerBg,borderRadius:DS.radiusCard,padding:"14px",marginBottom:10,border:`1.5px solid ${DS.danger}33`,position:"relative"}}>
              {/* Numéro + supprimer */}
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                <div style={{fontSize:10,fontWeight:700,color:DS.danger,textTransform:"uppercase",letterSpacing:.5}}>Dégradation {idx+1}</div>
                {degs.length > 1 && !degLocked(deg) && (
                  <button onClick={() => removeDeg(idx)} style={{background:"none",border:"none",color:DS.danger,fontSize:18,cursor:"pointer",lineHeight:1,padding:0}}>×</button>
                )}
              </div>

              {/* Type — chips */}
              <div style={{marginBottom:10}}>
                <div style={{fontSize:10,color:DS.textMuted,marginBottom:6,fontWeight:600,textTransform:"uppercase",letterSpacing:.5}}>Type</div>
                <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
                  {INCIDENT_TYPES.map(t => (
                    <button key={t} onClick={() => updateDeg(idx, "type", t)} disabled={degLocked(deg)}
                      style={{padding:"5px 9px",border:`1.5px solid ${deg.type===t?DS.danger:DS.border}`,borderRadius:7,background:deg.type===t?DS.dangerBg:DS.card,color:deg.type===t?DS.dangerText:DS.textSecondary,fontWeight:deg.type===t?700:500,fontSize:10,cursor:"pointer",fontFamily:DS.fontBody,...(degLocked(deg)?lockedStyle:{})}}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              {/* Nombre + Précision */}
              <div style={{display:"grid",gridTemplateColumns:"80px 1fr",gap:8,marginBottom:10}}>
                <div>
                  <div style={{fontSize:10,color:DS.textMuted,marginBottom:4,fontWeight:600,textTransform:"uppercase",letterSpacing:.5}}>Qté</div>
                  <input type="number" min="1" value={deg.nombre} onChange={e => updateDeg(idx, "nombre", e.target.value)} placeholder="1"
                    disabled={degLocked(deg)} style={{...si,padding:"7px 10px",fontSize:12, ...(degLocked(deg)?lockedStyle:{})}}/>
                </div>
                <div>
                  <div style={{fontSize:10,color:DS.textMuted,marginBottom:4,fontWeight:600,textTransform:"uppercase",letterSpacing:.5}}>Précision</div>
                  <input value={deg.precision} onChange={e => updateDeg(idx, "precision", e.target.value)} placeholder="Détails optionnels..."
                    disabled={degLocked(deg)} style={{...si,padding:"7px 10px",fontSize:12, ...(degLocked(deg)?lockedStyle:{})}}/>
                </div>
              </div>

              {/* ── PHOTOS DE CET ARTICLE (agent) — 2 minimum, PAS un maximum ── */}
              {useMenageMiniQueue && (() => {
                const degPhotos = deg.photos || []
                const manque = Math.max(0, PHOTOS_MIN_MENAGE - degPhotos.length)
                return (
                  <div data-testid={`incident-photos-deg-${idx}`}
                    style={{marginBottom:10,padding:'9px 11px',background:manque>0?DS.dangerBg:DS.successBg,borderRadius:8,border:`1px solid ${manque>0?DS.danger+'44':DS.success+'44'}`}}>
                    <div style={{fontSize:11.5,fontWeight:700,color:manque>0?DS.dangerText:DS.successText,display:'flex',alignItems:'center',gap:6}}>
                      <Camera size={13} strokeWidth={2.2}/> Photos : {degPhotos.length}
                      {manque > 0
                        ? <span style={{fontWeight:600,fontSize:10.5}}>— encore {manque} pour atteindre le minimum</span>
                        : <span style={{fontWeight:600,fontSize:10.5,display:'inline-flex',alignItems:'center',gap:3}}><Check size={11} strokeWidth={2.8}/> minimum atteint</span>}
                    </div>
                    {/* ⚠️ 2 EST UN MINIMUM, PAS UN MAXIMUM (Dimitri) — le message le dit. */}
                    <div style={{fontSize:10,color:DS.textMuted,marginTop:3}}>
                      2 photos minimum par article — ajoutez-en autant qu'il en faut pour prouver.
                    </div>
                    {degPhotos.length > 0 && (
                      <div style={{display:'flex',flexWrap:'wrap',gap:6,marginTop:8}}>
                        {degPhotos.map((pp, j) => (
                          <div key={pp.path} style={{position:'relative',width:56,height:56,borderRadius:6,overflow:'hidden',border:`1px solid ${DS.border}`,background:DS.card}}>
                            {(pp.previewUrl || pp.publicUrl) ? (
                              <img src={pp.previewUrl || pp.publicUrl} alt={`preuve ${j+1}`} style={{width:'100%',height:'100%',objectFit:'cover'}}/>
                            ) : (
                              <div style={{width:'100%',height:'100%',display:'flex',alignItems:'center',justifyContent:'center',color:DS.textMuted}}><Camera size={16} strokeWidth={2}/></div>
                            )}
                            <button type="button" onClick={() => handleRemovePhotoDeg(idx, j)}
                              disabled={uploading || submitting || fullyLocked}
                              aria-label={`Supprimer photo ${j+1}`}
                              style={{position:'absolute',top:2,right:2,background:DS.danger,color:'#fff',border:'none',borderRadius:'50%',width:16,height:16,fontSize:9,fontWeight:700,cursor:'pointer',lineHeight:1,display:'flex',alignItems:'center',justifyContent:'center'}}>×</button>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* DEUX CHEMINS : l'appareil ET la galerie (une photo prise
                        deux minutes avant vaut autant qu'une photo prise là). */}
                    <div style={{display:'flex',gap:6,marginTop:8}}>
                      <button type="button" className="fbz-press" data-testid={`incident-photo-appareil-${idx}`}
                        onClick={() => ouvrirAppareil(idx)}
                        disabled={uploading || submitting || fullyLocked || degLocked(deg)}
                        style={{flex:1,display:'inline-flex',alignItems:'center',justifyContent:'center',gap:6,padding:'11px 10px',border:`1.5px dashed ${DS.ciel}`,background:'transparent',color:DS.marine,borderRadius:DS.radiusControl,fontSize:11.5,fontWeight:700,cursor:'pointer',fontFamily:DS.fontBody}}>
                        {uploading ? <Loader2 size={13}/> : <Camera size={14} strokeWidth={2.2}/>} Appareil
                      </button>
                      <button type="button" className="fbz-press" data-testid={`incident-photo-galerie-${idx}`}
                        onClick={() => ouvrirGalerie(idx)}
                        disabled={uploading || submitting || fullyLocked || degLocked(deg)}
                        style={{flex:1,display:'inline-flex',alignItems:'center',justifyContent:'center',gap:6,padding:'11px 10px',border:`1.5px dashed ${DS.border}`,background:'transparent',color:DS.textSecondary,borderRadius:DS.radiusControl,fontSize:11.5,fontWeight:700,cursor:'pointer',fontFamily:DS.fontBody}}>
                        <Plus size={14} strokeWidth={2.2}/> Galerie
                      </button>
                    </div>
                  </div>
                )
              })()}

              {/* Statut ménage — ADR-228 : le JUGEMENT par dégradation passe de
                  l'agent à l'ORCHESTRATEUR/ADMIN. Le sélecteur d'état n'est plus
                  visible côté agent (déclaration seule ; défaut « À vérifier »
                  forcé). Admin conserve le bloc (suivi + jugement). */}
              {isAdmin && (
                <div>
                  <div style={{fontSize:10,color:DS.textMuted,marginBottom:6,fontWeight:600,textTransform:"uppercase",letterSpacing:.5}}>État</div>
                  <div style={{display:"flex",gap:6}}>
                    {["À vérifier", "Non récupérable"].map(s => {
                      const colorKey = s === "À vérifier" ? "Récupérable" : s
                      const c = MENAGE_STATUS_COLOR[colorKey] || DS.warning
                      const StatIco = s === "À vérifier" ? Search : Ban
                      return <button key={s} onClick={() => updateDeg(idx, "statutMenage", s)} disabled={degLocked(deg)}
                        style={{display:"inline-flex",alignItems:"center",justifyContent:"center",gap:5,flex:1,padding:"7px 10px",border:`1.5px solid ${deg.statutMenage===s?c:DS.border}`,borderRadius:7,background:deg.statutMenage===s?c+"18":DS.card,color:deg.statutMenage===s?c:DS.textMuted,fontWeight:700,fontSize:11,cursor:"pointer",fontFamily:DS.fontBody, ...(degLocked(deg)?lockedStyle:{})}}>
                        <StatIco size={13} strokeWidth={2.2}/>{s}
                      </button>
                    })}
                  </div>
                </div>
              )}

              {/* Section admin — uniquement si Non récupérable */}
              {isAdmin && deg.statutMenage === "Non récupérable" && (
                <div style={{marginTop:10,paddingTop:10,borderTop:`1px dashed ${DS.danger}44`}}>
                  <div style={{fontSize:10,color:DS.marine,fontWeight:700,textTransform:"uppercase",letterSpacing:.5,marginBottom:8}}>Suivi FULLBNBZEN</div>

                  {/* État financier (chantier ⑥ — 5 états, REMPLACE « Traitement »).
                      Sous-loc : sélecteur restreint à Sans coût / Achat FULLBNBZEN. */}
                  <div style={{marginBottom:8}}>
                    <div style={{fontSize:10,color:DS.textMuted,marginBottom:5,fontWeight:600,textTransform:"uppercase",letterSpacing:.5}}>État financier</div>
                    <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                      {etatOptions.map(val => (
                        <button key={val} onClick={() => updateDeg(idx, "etatFinancier", val)}
                          style={{display:"inline-flex",alignItems:"center",justifyContent:"center",gap:5,padding:"6px 9px",border:`1.5px solid ${deg.etatFinancier===val?DS.marine:DS.border}`,borderRadius:7,background:deg.etatFinancier===val?DS.cielSoft:DS.card,color:deg.etatFinancier===val?DS.marine:DS.textMuted,fontWeight:700,fontSize:10,cursor:"pointer",fontFamily:DS.fontBody}}>
                          {val}
                        </button>
                      ))}
                    </div>
                    {!deg.etatFinancier && <div style={{fontSize:9,color:DS.warningText,marginTop:4,fontWeight:600}}>⚠️ À qualifier — non pris en compte tant que non renseigné.</div>}
                    {(deg.etatFinancier === "Refacturé propriétaire" || deg.etatFinancier === "AirCover + refacturé") && <div style={{fontSize:9,color:DS.marine,marginTop:4,fontWeight:500}}>→ Refacturé au propriétaire (au brut, sans déduction AirCover).</div>}
                    {deg.etatFinancier === "Achat FULLBNBZEN" && <div style={{fontSize:9,color:DS.textMuted,marginTop:4,fontWeight:500}}>→ Coût absorbé par FULLBNBZEN (charge compta).</div>}
                    {deg.etatFinancier === "Remboursé AirCover" && <div style={{fontSize:9,color:DS.textMuted,marginTop:4,fontWeight:500}}>→ AirCover rembourse directement le propriétaire (hors facture).</div>}
                  </div>

                  {/* Remplacé le + Coût (états porteurs d'un coût FBZ uniquement) */}
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
                    <div>
                      <div style={{fontSize:10,color:DS.textMuted,marginBottom:4,fontWeight:600,textTransform:"uppercase",letterSpacing:.5}}>
                        {/* « DÉPOSÉ » — le mot de l'écran (décision Dimitri) ;
                            la facture garde « Remplacements » (fichier séparé). */}
                        {deg.remplaceLe ? "Déposé le" : "À déposer"}
                      </div>
                      <input type="date" value={deg.remplaceLe || ""} onChange={e => updateDeg(idx, "remplaceLe", e.target.value)}
                        style={{border:`0.5px solid ${DS.borderHover}`,borderRadius:DS.radiusControl,padding:"7px 10px",width:"100%",fontSize:12,outline:"none",fontFamily:DS.fontBody,background:deg.remplaceLe?DS.successBg:DS.card}}/>
                    </div>
                    {COUT_ETATS.has(deg.etatFinancier) && (
                      <div>
                        <div style={{fontSize:10,color:DS.textMuted,marginBottom:4,fontWeight:600,textTransform:"uppercase",letterSpacing:.5}}>Coût (€)</div>
                        <input type="number" min="0" step="0.01" value={deg.cout || ""} onChange={e => updateDeg(idx, "cout", e.target.value)} placeholder="0.00"
                          style={{border:`0.5px solid ${DS.borderHover}`,borderRadius:DS.radiusControl,padding:"7px 10px",width:"100%",fontSize:12,outline:"none",fontFamily:DS.fontBody,background:DS.card}}/>
                      </div>
                    )}
                  </div>
                  {/* AirCover — sous-loc : perçu par FBZ (atténuation charge) ;
                      hors sous-loc : perçu par le proprio (états AirCover). */}
                  {isSousLoc ? (
                    <div style={{marginTop:8}}>
                      <div style={{fontSize:10,color:DS.textMuted,marginBottom:4,fontWeight:600,textTransform:"uppercase",letterSpacing:.5}}>AirCover perçu par FULLBNBZEN (€)</div>
                      <input type="number" min="0" step="0.01" value={deg.aircoverFbz || ""} onChange={e => updateDeg(idx, "aircoverFbz", e.target.value)} placeholder="0.00"
                        style={{border:`0.5px solid ${DS.borderHover}`,borderRadius:DS.radiusControl,padding:"7px 10px",width:"100%",fontSize:12,outline:"none",fontFamily:DS.fontBody,background:DS.card}}/>
                      <div style={{fontSize:9,color:DS.textMuted,marginTop:4,fontWeight:500}}>→ En atténuation de la charge compta (FBZ est l'hôte Airbnb).</div>
                    </div>
                  ) : AIRCOVER_PROPRIO_ETATS.has(deg.etatFinancier) && (
                    <div style={{marginTop:8}}>
                      <div style={{fontSize:10,color:DS.textMuted,marginBottom:4,fontWeight:600,textTransform:"uppercase",letterSpacing:.5}}>Montant AirCover perçu par le proprio (€)</div>
                      <input type="number" min="0" step="0.01" value={deg.aircoverMontant || ""} onChange={e => updateDeg(idx, "aircoverMontant", e.target.value)} placeholder="0.00"
                        style={{border:`0.5px solid ${DS.borderHover}`,borderRadius:DS.radiusControl,padding:"7px 10px",width:"100%",fontSize:12,outline:"none",fontFamily:DS.fontBody,background:DS.card}}/>
                    </div>
                  )}
                  {deg.remplaceLe && <div style={{display:"flex",alignItems:"center",gap:5,fontSize:10,color:DS.successText,fontWeight:700,marginTop:5}}><Check size={12} strokeWidth={2.6}/>Déposé le {new Date(deg.remplaceLe).toLocaleDateString("fr-FR")}{parseFloat(deg.cout) > 0 ? ` · ${parseFloat(deg.cout).toFixed(2)} €` : ""}</div>}
                </div>
              )}

              {/* Bloc SUIVI lecture-seule côté ménage */}
              {!isAdmin && deg.statutMenage === "Non récupérable" && deg.remplaceLe && (
                <div style={{marginTop:10,paddingTop:10,borderTop:`1px dashed ${DS.danger}44`}}>
                  <div style={{fontSize:10,color:DS.marine,fontWeight:700,textTransform:"uppercase",letterSpacing:.5,marginBottom:8}}>Suivi FULLBNBZEN</div>
                  <div>
                    <div style={{fontSize:10,color:DS.textMuted,marginBottom:4,fontWeight:600,textTransform:"uppercase",letterSpacing:.5}}>Déposé le</div>
                    <div style={{border:`0.5px solid ${DS.borderHover}`,borderRadius:DS.radiusControl,padding:"7px 10px",width:"100%",fontSize:12,fontFamily:DS.fontBody,background:DS.successBg,color:DS.textSecondary,boxSizing:"border-box"}}>
                      {new Date(deg.remplaceLe).toLocaleDateString("fr-FR")}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div style={{display:"flex",gap:8}}>
          <button className="fbz-press" onClick={handleCancel} disabled={submitting || uploading}
            style={{flex:1,padding:12,background:DS.card,color:DS.marine,border:`0.5px solid ${DS.borderHover}`,borderRadius:DS.radiusControl,fontWeight:600,cursor:(submitting||uploading)?"not-allowed":"pointer",fontFamily:DS.fontBody,opacity:(submitting||uploading)?0.6:1}}>
            Annuler
          </button>
          <button className="fbz-press" onClick={handleSave}
            disabled={fullyLocked || submitting || uploading || photosMinNotMet}
            title={
              timeLocked && lockDateForBanner
                ? `Verrouillé jusqu'au ${formatDateShort(lockDateForBanner)}`
                : photosMinNotMet ? `Minimum ${PHOTOS_MIN_MENAGE} photos requises`
                : uploading ? 'Upload photo en cours…' : ''
            }
            style={{
              display:"inline-flex",
              alignItems:"center",
              justifyContent:"center",
              gap:7,
              flex:2,
              padding:12,
              background: (fullyLocked || photosMinNotMet) ? DS.borderHover : DS.corail,
              color:"#fff",
              border:"none",
              borderRadius:DS.radiusControl,
              fontWeight:700,
              cursor: (fullyLocked || submitting || uploading || photosMinNotMet) ? 'not-allowed' : 'pointer',
              fontFamily:DS.fontBody,
              opacity: (submitting || uploading) ? 0.7 : 1,
            }}>
            {submitting ? <><Loader2 size={15}/>Enregistrement…</>
              : uploading ? <><Loader2 size={15}/>Upload…</>
              : (timeLocked && lockDateForBanner) ? <><Lock size={15} strokeWidth={2.3}/>Le {formatDateShort(lockDateForBanner)}</>
              : <><Save size={15} strokeWidth={2.2}/>Enregistrer</>}
          </button>
        </div>
      </div>
    </div>
  )
}
